import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import {
  ActivityIndicator,
  Button,
  Menu,
  Text,
  TextInput
} from 'react-native-paper';
import type {
  ReviewCategory,
  ReviewItem,
  ReviewTemplate
} from './shareItemMapping';

export interface TargetTemplateSelectorProps {
  availableTemplates: ReviewTemplate[];
  selectedTemplateId: number | null;
  selectedTemplateName?: string;
  menuVisible: boolean;
  onOpenMenu: () => void;
  onDismissMenu: () => void;
  onSelectTemplate: (template: ReviewTemplate) => void;
}

export const TargetTemplateSelector: React.FC<TargetTemplateSelectorProps> = ({
  availableTemplates,
  selectedTemplateId,
  selectedTemplateName,
  menuVisible,
  onOpenMenu,
  onDismissMenu,
  onSelectTemplate
}) => (
  <View style={styles.section}>
    <Text style={styles.label}>1. Select Target Template:</Text>
    <Menu
      visible={menuVisible}
      onDismiss={onDismissMenu}
      anchor={
        <Button
          testID="share-target-template-selector"
          mode="outlined"
          onPress={onOpenMenu}
          style={styles.selector}
          contentStyle={styles.selectorContent}
        >
          {selectedTemplateId
            ? selectedTemplateName || `Template ${selectedTemplateId}`
            : 'Select Template'}
        </Button>
      }
    >
      {availableTemplates.map((template) => (
        <Menu.Item
          testID={`share-target-template-${template.id}`}
          key={template.id}
          title={`${template.name} (${template.type})`}
          onPress={() => onSelectTemplate(template)}
        />
      ))}
    </Menu>
  </View>
);

export interface TargetCategorySelectorProps {
  categories: ReviewCategory[];
  selectedCategoryId: number | null;
  selectedCategoryName?: string;
  loadingTemplate: boolean;
  menuVisible: boolean;
  onOpenMenu: () => void;
  onDismissMenu: () => void;
  onSelectCategory: (category: ReviewCategory) => void;
}

export const TargetCategorySelector: React.FC<TargetCategorySelectorProps> = ({
  categories,
  selectedCategoryId,
  selectedCategoryName,
  loadingTemplate,
  menuVisible,
  onOpenMenu,
  onDismissMenu,
  onSelectCategory
}) => (
  <View style={styles.section}>
    <Text style={styles.label}>2. Select Target Category:</Text>
    {loadingTemplate ? (
      <View style={styles.loadingSection}>
        <ActivityIndicator size="small" />
        <Text style={styles.loadingText}>Loading categories...</Text>
      </View>
    ) : (
      <Menu
        visible={menuVisible}
        onDismiss={onDismissMenu}
        anchor={
          <Button
            testID="share-target-category-selector"
            mode="outlined"
            onPress={onOpenMenu}
            style={styles.selector}
            contentStyle={styles.selectorContent}
            disabled={loadingTemplate || categories.length === 0}
          >
            {selectedCategoryId
              ? selectedCategoryName || `Category ${selectedCategoryId}`
              : categories.length === 0
                ? 'No categories found'
                : 'Select Category'}
          </Button>
        }
      >
        {categories.map((category) => (
          <Menu.Item
            testID={`share-target-category-${category.id}`}
            key={category.id}
            title={category.name}
            onPress={() => onSelectCategory(category)}
          />
        ))}
      </Menu>
    )}
  </View>
);

export interface TargetItemSelectorProps {
  items: ReviewItem[];
  selectedItemId: number | null;
  selectedItemName?: string;
  menuVisible: boolean;
  onOpenMenu: () => void;
  onDismissMenu: () => void;
  onSelectItem: (item: ReviewItem) => void;
  onCreateNewItem: () => void;
}

export const TargetItemSelector: React.FC<TargetItemSelectorProps> = ({
  items,
  selectedItemId,
  selectedItemName,
  menuVisible,
  onOpenMenu,
  onDismissMenu,
  onSelectItem,
  onCreateNewItem
}) => (
  <View style={styles.section}>
    <Text style={styles.label}>3. Select Target Item:</Text>
    <View style={styles.itemSelectorRow}>
      <Menu
        visible={menuVisible}
        onDismiss={onDismissMenu}
        anchor={
          <Button
            testID="share-target-item-selector"
            mode="outlined"
            onPress={onOpenMenu}
            style={[styles.selector, styles.itemDropdown]}
            contentStyle={styles.selectorContent}
          >
            {selectedItemId
              ? selectedItemName || `Item ${selectedItemId}`
              : items.length === 0
                ? 'No items in this category'
                : 'Select Existing Item'}
          </Button>
        }
      >
        <ScrollView style={styles.menuScrollArea}>
          {items.map((item) => (
            <Menu.Item
              testID={`share-target-item-${item.id}`}
              key={item.id}
              title={item.name}
              onPress={() => onSelectItem(item)}
            />
          ))}
        </ScrollView>
      </Menu>
      <Button
        testID="share-create-new-item-button"
        mode="contained"
        onPress={onCreateNewItem}
        style={styles.createItemButton}
        icon="plus"
      >
        Create New Item
      </Button>
    </View>
  </View>
);

export interface CreateTargetItemFormProps {
  newItemName: string;
  newItemDescription: string;
  creatingItem: boolean;
  onChangeName: (value: string) => void;
  onChangeDescription: (value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
}

export const CreateTargetItemForm: React.FC<CreateTargetItemFormProps> = ({
  newItemName,
  newItemDescription,
  creatingItem,
  onChangeName,
  onChangeDescription,
  onCancel,
  onCreate
}) => (
  <View style={styles.section}>
    <View style={styles.createItemHeader}>
      <Text style={styles.label}>3. Create New Item:</Text>
      <Button mode="text" onPress={onCancel} compact>
        Cancel
      </Button>
    </View>

    <View style={styles.createItemForm}>
      <TextInput
        label="Item Name"
        value={newItemName}
        onChangeText={onChangeName}
        mode="outlined"
        style={styles.input}
      />
      <TextInput
        label="Description (Optional)"
        value={newItemDescription}
        onChangeText={onChangeDescription}
        mode="outlined"
        multiline
        numberOfLines={2}
        style={styles.input}
      />
      <Button
        testID="share-create-item-continue-button"
        mode="contained"
        onPress={onCreate}
        loading={creatingItem}
        disabled={!newItemName.trim() || creatingItem}
        icon="check"
      >
        Create Item and Continue
      </Button>
    </View>
  </View>
);

const styles = StyleSheet.create({
  section: {
    marginBottom: 20
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333'
  },
  selector: {
    width: '100%'
  },
  selectorContent: {
    justifyContent: 'flex-start'
  },
  loadingSection: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 12
  },
  loadingText: {
    fontSize: 14,
    color: '#666'
  },
  itemSelectorRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start'
  },
  itemDropdown: {
    flex: 1
  },
  createItemButton: {
    alignSelf: 'flex-start'
  },
  createItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12
  },
  createItemForm: {
    gap: 12,
    padding: 16,
    backgroundColor: '#F5F5F5',
    borderRadius: 8
  },
  input: {
    backgroundColor: 'white'
  },
  menuScrollArea: {
    maxHeight: 300
  }
});
